import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import ErrorBoundary from './components/common/ErrorBoundary';
import Loading from './components/common/Loading';
import useAuthStore from './store/authStore';
import { routerConfig } from './config/router';
import Injuries from './pages/Injuries';
const Talleres = lazy(() => import('./pages/Talleres'));

// Lazy loading para optimizar la carga inicial
const Login = lazy(() => import('./pages/Login'));
const Layout = lazy(() => import('./components/layout/Layout'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Traceability = lazy(() => import('./pages/Traceability'));
const Reports = lazy(() => import('./pages/Reports'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Settings = lazy(() => import('./pages/Settings'));
const Help = lazy(() => import('./pages/Help'));
const Alertas = lazy(() => import('./pages/Alertas'));
const CardSearch = lazy(() => import('./pages/CardSearch'));
const Fixtures = lazy(() => import('./pages/Fixtures'));
const Decos = lazy(() => import('./pages/Decos'));
const HotLeagues = lazy(() => import('./pages/HotLeagues'));
const Playoffs = lazy(() => import('./pages/Playoffs'));
const Contratos = lazy(() => import('./pages/Contratos'));
const Agentes = lazy(() => import('./pages/Agentes'));
const Cuotas = lazy(() => import('./pages/Cuotas'));
const Escenarios = lazy(() => import('./pages/Escenarios'));
const Integrity = lazy(() => import('./pages/Integrity'));
const Connections = lazy(() => import('./pages/Connections'));
const NotasInformativas = lazy(() => import('./pages/NotasInformativas'));
const Cobertura = lazy(() => import('./pages/Cobertura'));
const Mercado = lazy(() => import('./pages/Mercado'));
const Amistosos = lazy(() => import('./pages/Amistosos'));
const Informe = lazy(() => import('./pages/Informe'));
const FichajesRiesgo = lazy(() => import('./pages/FichajesRiesgo'));
const Clausulas = lazy(() => import('./pages/Clausulas'));


// Componente de error fallback
const ErrorFallback = ({ error, resetErrorBoundary }) => (
  <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 flex items-center justify-center p-4">
    <div className="max-w-md w-full bg-white dark:bg-secondary-800 rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-center w-16 h-16 bg-error-100 dark:bg-error-900/20 rounded-full mx-auto mb-4">
        <span className="text-2xl">⚠️</span>
      </div>

      <h2 className="text-xl font-semibold text-center text-secondary-900 dark:text-white mb-2">
        ¡Algo salió mal!
      </h2>

      <p className="text-secondary-600 dark:text-secondary-400 text-center mb-4">
        Ha ocurrido un error inesperado. Puedes intentar recargar la página.
      </p>

      <div className="flex gap-3">
        <button
          onClick={resetErrorBoundary}
          className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
        >
          Reintentar
        </button>
        <button
          onClick={() => window.location.reload()}
          className="flex-1 bg-secondary-200 dark:bg-secondary-700 hover:bg-secondary-300 dark:hover:bg-secondary-600 text-secondary-700 dark:text-secondary-300 font-medium py-2 px-4 rounded-lg transition-colors duration-200"
        >
          Recargar
        </button>
      </div>

      {process.env.NODE_ENV === 'development' && (
        <details className="mt-4">
          <summary className="text-sm text-secondary-500 cursor-pointer">
            Detalles del error (desarrollo)
          </summary>
          <pre className="text-xs text-error-600 mt-2 overflow-auto">
            {error.message}
          </pre>
        </details>
      )}
    </div>
  </div>
);

// Componente de loading para lazy loading
const PageLoading = () => (
  <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 flex items-center justify-center">
    <Loading
      variant="football"
      size="lg"
      text="Cargando página..."
    />
  </div>
);

// Componente principal de la aplicación
function App() {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, errorInfo) => {
        // Aquí podrías enviar el error a un servicio de logging
        console.error('Error capturado por ErrorBoundary:', error, errorInfo);
      }}
    >
      <BrowserRouter {...routerConfig}>
        <div className="App">
          <Suspense fallback={<PageLoading />}>
            <Routes>

              {/* Ruta de Login */}
              <Route path="/login" element={<Login />} />

              {/* Rutas protegidas dentro del Layout */}
              <Route path="/" element={<Layout />}>

                {/* Redirección por defecto - PERSONALIZADA por usuario */}
                <Route index element={<DefaultRedirect />} />

                {/* Dashboard */}
                <Route path="dashboard" element={<Dashboard />} />

                {/* Trazabilidad */}
                <Route path="traceability" element={<Traceability />} />

                {/* Informes */}
                <Route path="reports" element={<Reports />}>
                  {/* Subrutas de informes si las necesitamos */}
                  {/* <Route path="new" element={<NewReport />} />
                  <Route path=":id" element={<ReportDetail />} />
                  <Route path=":id/edit" element={<EditReport />} /> */}
                </Route>

                {/* Análisis de Tarjetas - NUEVA RUTA */}
                <Route path="card-search" element={<CardSearch />} />

                {/* Alertas - NUEVA RUTA */}
                <Route path="alerts" element={<Alertas />} />
                <Route path="notas-informativas" element={<NotasInformativas />} />

                {/* Analytics (solo admin) */}
                <Route path="analytics" element={<Analytics />} />

                {/* Configuración (solo admin) */}
                <Route path="settings" element={<Settings />} />

                {/* Ayuda */}
                <Route path="help" element={<Help />} />

                {/* Ruta catch-all para 404 */}
                <Route path="*" element={<NotFound />} />

                {/* Partidos - NUEVO */}
                <Route path="fixtures" element={<Fixtures />} />

                {/* Talleres - NUEVA RUTA 🆕 */}
                <Route path="talleres" element={<Talleres />} />

                {/* Injuries - NUEVA RUTA 🆕 */}
                <Route path="injuries" element={<Injuries />} />

                {/* Decos - NUEVA RUTA 🆕 */}
                <Route path="decos" element={<Decos />} />

                {/* Ligas Calientes - NUEVA RUTA 🔥 */}
                <Route path="hot-leagues" element={<HotLeagues />} />

                {/* Playoffs 🏆 */}
                <Route path="playoffs" element={<Playoffs />} />

                {/* fichajes-riesgo */}
                <Route path="/fichajes-riesgo" element={<FichajesRiesgo />} />

                {/* Contratos */}
                <Route path="contratos" element={<Contratos />} />

                {/* Agentes */}
                <Route path="agentes" element={<Agentes />} />

                {/* Agentes */}
                <Route path="cuotas" element={<Cuotas />} />

                {/* Escenarios de descenso */}
                <Route path="escenarios" element={<Escenarios />} />

                {/* Integrity */}
                <Route path="integrity" element={<Integrity />} />

                {/* Conexiones - solo juan@temis.com y admin */}
                <Route path="connections" element={<Connections />} />

                {/* Cobertura - solo admin */}
                <Route path="cobertura" element={<Cobertura />} />

                {/* Mercado por Categoría - solo Admin */}
                <Route path="mercado" element={<Mercado />} />

                {/* Amistosos - solo Admin */}
                <Route path="amistosos" element={<Amistosos />} />

                {/* Informes - solo Admin */}
                <Route path="informe" element={<Informe />} />

                {/* Cláusulas - solo Admin */}
                <Route path="clausulas" element={<Clausulas />} />


              </Route>



            </Routes>
          </Suspense>
        </div>
      </BrowserRouter>

      {/* Toaster para notificaciones */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--toast-bg)',
            color: 'var(--toast-color)',
            border: '1px solid var(--toast-border)',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#ffffff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#ffffff',
            },
          },
        }}
      />
    </ErrorBoundary>
  );
}

// Componente para redirección personalizada basada en el usuario
const DefaultRedirect = () => {
  const { user } = useAuthStore();

  // Si es el usuario específico de alertas, redirigir a /alerts
  if (user?.email === 'user@temis.com') {
    return <Navigate to="/alerts" replace />;
  }

  // Para todos los demás usuarios, redirigir al dashboard
  return <Navigate to="/dashboard" replace />;
};

// Componente 404
const NotFound = () => {
  return (
    <div className="min-h-full flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <div className="w-24 h-24 bg-secondary-100 dark:bg-secondary-800 rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="text-4xl">🔍</span>
        </div>

        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white mb-2">
          Página no encontrada
        </h1>

        <p className="text-secondary-600 dark:text-secondary-400 mb-6">
          La página que buscas no existe o ha sido movida.
        </p>

        <div className="flex gap-3 justify-center">
          <button
            onClick={() => window.history.back()}
            className="bg-secondary-200 dark:bg-secondary-700 hover:bg-secondary-300 dark:hover:bg-secondary-600 text-secondary-700 dark:text-secondary-300 font-medium py-2 px-4 rounded-lg transition-colors duration-200"
          >
            Volver
          </button>
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
          >
            Ir al inicio
          </button>
        </div>
      </div>
    </div>
  );
};

export default App;